import { toast } from 'sonner'
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Sparkles,
  X,
} from 'lucide-react'
import { LucideIcon } from 'lucide-react'
import Text from '@/components/custom-ui/text'
import { cn } from '@/lib/utils'

export type ToastVariant = 'info' | 'success' | 'warning' | 'error'

type ShowToastOptions = {
  variant?: ToastVariant
  title?: string
  duration?: number
}

type VariantStyle = {
  Icon: LucideIcon
  iconClass: string
}

const VARIANTS: Record<ToastVariant, VariantStyle> = {
  info: {
    Icon: Sparkles,
    iconClass: 'text-primary-500',
  },
  success: {
    Icon: CheckCircle2,
    iconClass: 'text-[hsl(var(--success-normal))]',
  },
  warning: {
    Icon: AlertTriangle,
    iconClass: 'text-[hsl(var(--alert-500))]',
  },
  error: {
    Icon: AlertCircle,
    iconClass: 'text-[hsl(var(--error-normal))]',
  },
}

export const showToast = (text: string, options: ShowToastOptions = {}) => {
  const { variant = 'info', title, duration = 5000 } = options
  const { Icon, iconClass } = VARIANTS[variant]

  return toast.custom(
    (t) => (
      <div
        role={variant === 'error' || variant === 'warning' ? 'alert' : 'status'}
        className="pointer-events-auto flex w-[360px] max-w-[calc(100vw-2rem)] items-start gap-3 rounded-md border border-primary-600 bg-secondary-500 px-4 py-3.5 shadow-card-lg"
      >
        <Icon
          className={cn('mt-0.5 shrink-0', iconClass)}
          size={20}
          strokeWidth={1.75}
          aria-hidden
        />
        <div className="flex flex-1 flex-col gap-1">
          {title && (
            <Text
              as="span"
              size={12}
              lineHeight={16}
              fontWeight="semiBold"
              uppercase
              className="tracking-widest text-white"
            >
              {title}
            </Text>
          )}
          <Text
            as="span"
            size={14}
            lineHeight={20}
            className="tracking-[calc(0.25/16*1rem)] text-secondary-50"
          >
            {text}
          </Text>
        </div>
        <button
          type="button"
          onClick={() => toast.dismiss(t)}
          aria-label="Dismiss notification"
          className="-mr-1 -mt-1 shrink-0 rounded-sm p-1 text-secondary-50 transition-colors hover:bg-secondary-400 hover:text-white"
        >
          <X size={16} strokeWidth={1.75} />
        </button>
      </div>
    ),
    { duration },
  )
}
